import type {
  ButtonBackground,
  ButtonColor,
  ButtonStyle,
} from '@tranquility-ai/theme-components/button';

/** Standard props for title-bar actions on the brand-blue surface. */
export const navBarButtonProps = {
  style: 'text' as ButtonStyle,
  color: 'light' as ButtonColor,
  background: 'brand' as ButtonBackground,
};

/**
 * Layout-only classes for vertical icon+label nav items (Account, Import…).
 * Color, border, and hover fills come from navBarButtonProps — do not layer
 * `bg-*` / `opacity-*` / `hover:bg-*` on top.
 */
export const navBarVerticalButtonClassName = 'h-auto min-h-0 gap-0 px-1 py-1';

/** Inner stack — theme Button wraps children in a single span. */
export const navBarVerticalStackClassName =
  'flex flex-col items-center justify-center gap-0 text-xs leading-none';

export const navBarVerticalIconClassName =
  'flex h-[28px] w-[28px] shrink-0 items-center justify-center p-1';

/** Inline text nav item (Sync Data on wide viewports). */
export const navBarItemButtonClassName =
  'h-fit w-fit sm:pl-1 pl-3 pr-2 py-1 tracking-wider text-[14px] items-center gap-2';

/**
 * Overflow menu row layout. HeroUI DropdownItem hover styles target this class
 * via `[&_.nav-bar-overflow-item]` — keep the class name stable if you change
 * the selector in navigation-bar-horizontal.tsx.
 */
export const navBarOverflowMenuItemClassName =
  'nav-bar-overflow-item flex flex-row items-center gap-2';

/** Assistant / Work Products pill toggles inside the case selector cluster. */
export const navBarSegmentButtonClassName = (selected: boolean) =>
  `flex flex-row items-center gap-2 px-2 h-[30px] rounded-full border border-solid focus-visible:outline-none focus-visible:outline-offset-0 ${
    selected ? 'border-white' : 'border-transparent font-normal'
  }`;

/** Cases / CSAM / Analytics vertical items on the brand-blue title bar. */
export function navBarPrimaryNavButtonProps(selected: boolean): {
  style: ButtonStyle;
  color: ButtonColor;
  background: ButtonBackground;
} {
  return {
    style: selected ? 'outline' : 'text',
    color: 'light',
    background: 'brand',
  };
}

export const navBarVerticalNavButtonClassName = (selected: boolean) =>
  `${navBarVerticalButtonClassName}${selected ? ' rounded-lg border border-solid border-white' : ''}`;

export const navBarPrimaryNavLabelClassName = (selected: boolean, largeText?: boolean) =>
  `whitespace-nowrap${largeText ? ' text-[18px]' : ''}${selected ? ' font-semibold' : ''}`;

/** External help link styled like a vertical nav item. */
export const navBarExternalLinkClassName = `${navBarVerticalStackClassName} no-underline text-inherit px-1 py-1`;

/** Full-width rows inside Import / uploader dropdown panels (inverse surface). */
export const navBarMenuPanelItemClassName =
  'flex w-full flex-row items-center justify-start gap-2 px-4 py-2 text-left text-sm';
